// 分拣看板本地同步工具：记录入库记录与分拣看板之间的增量关系，并在作废/修改时回滚或重算

type SortingBoardItem = {
  sortingGroup: string;
  sortingGroupSystemCode: string;
  temporaryLocation: number;
  inventoryUnitCode: string;
  inventoryUnitName?: string;
  sortedQuantity: number;
  displaySortingBarcode?: string;
  sortingBarcodes?: string[];
};

type BoardState = {
  sortingDatabase: SortingBoardItem[];
  nextLocationNumber?: number;
  reusableLocationPool?: number[];
};

export type SortingDelta = {
  inventoryUnitCode: string;
  quantity: number; // 正数表示增加，负数表示减少（仅在内部使用，外部存为正数）
  temporaryLocation: number;
  sortingGroupSystemCode?: string;
};

type RecordIndexItem = {
  recordId: number;
  deltas: SortingDelta[]; // 外部存储均为正数数量（代表该记录“新增”的数量分布）
  version: number;
  updatedAt: string;
};

type RecordIndex = Record<string, RecordIndexItem>; // key: recordId

const BOARD_KEY = 'inbound_scan_board_v1';
// 次要存储键：扫描页的标签页持久化机制使用
const SECONDARY_BOARD_KEY = 'inbound_scan_sorting_board_v1';
const INDEX_KEY = 'inbound_scan_board_record_index_global_v1';

function loadBoardState(): BoardState | null {
  try {
    const raw = localStorage.getItem(BOARD_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    return obj?.data || null;
  } catch {
    return null;
  }
}

function saveBoardState(state: BoardState) {
  try {
    const payload = { version: 1, updatedAt: new Date().toISOString(), data: state };
    localStorage.setItem(BOARD_KEY, JSON.stringify(payload));
  } catch {}
}

function saveBoardStateSecondary(state: BoardState) {
  try {
    const payload = { version: '1.0.0', lastUpdate: new Date().toISOString(), data: state } as any;
    localStorage.setItem(SECONDARY_BOARD_KEY, JSON.stringify(payload));
  } catch {}
}

function loadRecordIndex(): RecordIndex {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return obj && typeof obj === 'object' ? obj : {};
  } catch {
    return {};
  }
}

function saveRecordIndex(index: RecordIndex) {
  try {
    localStorage.setItem(INDEX_KEY, JSON.stringify(index));
  } catch {}
}

// 聚合相同 (unit+location+group) 的增量
export function aggregateDeltas(deltas: SortingDelta[]): SortingDelta[] {
  const map = new Map<string, SortingDelta>();
  for (const d of deltas) {
    const key = `${d.inventoryUnitCode}::${d.temporaryLocation}::${d.sortingGroupSystemCode || ''}`;
    const hit = map.get(key);
    if (hit) {
      hit.quantity += d.quantity;
    } else {
      map.set(key, { ...d });
    }
  }
  return Array.from(map.values()).filter(it => Number(it.quantity) > 0);
}

// 从扫描历史构建增量（正数数量）
export function buildDeltasFromScanHistory(scanHistory: Array<{ inventoryUnitCode: string; quantity: number; temporaryLocation: number; sortingGroupSystemCode?: string }>): SortingDelta[] {
  const raw: SortingDelta[] = (scanHistory || []).map(r => ({
    inventoryUnitCode: r.inventoryUnitCode,
    quantity: Number(r.quantity || 0),
    temporaryLocation: Number(r.temporaryLocation),
    sortingGroupSystemCode: r.sortingGroupSystemCode
  })).filter(it => it.inventoryUnitCode && it.temporaryLocation && it.quantity > 0);
  return aggregateDeltas(raw);
}

// 将增量应用到看板（sign=+1/-1）
export function applyDeltasToBoard(board: BoardState, deltas: SortingDelta[], sign: 1 | -1): BoardState {
  if (!board || !Array.isArray(board.sortingDatabase)) return board;
  const prevItems: SortingBoardItem[] = [...board.sortingDatabase];
  const prevLocations = new Set<number>(prevItems.map(it => Number(it.temporaryLocation)));
  const next: SortingBoardItem[] = [...prevItems];

  for (const d of deltas) {
    const matchIndex = next.findIndex(it =>
      it.inventoryUnitCode === d.inventoryUnitCode &&
      Number(it.temporaryLocation) === Number(d.temporaryLocation) &&
      (!d.sortingGroupSystemCode || it.sortingGroupSystemCode === d.sortingGroupSystemCode)
    );

    if (matchIndex >= 0) {
      const curr = next[matchIndex];
      const updatedQty = Math.max(0, Number(curr.sortedQuantity || 0) + sign * Number(d.quantity || 0));
      if (updatedQty === 0) {
        next.splice(matchIndex, 1);
      } else {
        next[matchIndex] = { ...curr, sortedQuantity: updatedQty };
      }
    } else if (sign > 0) {
      // 正向增加但未命中：降级为按最小信息新增一行（保持最简逻辑）
      next.push({
        sortingGroup: '-',
        sortingGroupSystemCode: d.sortingGroupSystemCode || '-',
        temporaryLocation: d.temporaryLocation,
        inventoryUnitCode: d.inventoryUnitCode,
        sortedQuantity: Number(d.quantity || 0)
      } as SortingBoardItem);
    } else {
      // 负向减少但未命中：忽略（最稳妥：不做额外猜测）
    }
  }

  // 过滤掉数量为0的行
  const nonZeroItems = next.filter(it => Number(it.sortedQuantity || 0) > 0);
  const newLocations = new Set<number>(nonZeroItems.map(it => Number(it.temporaryLocation)));
  const pool = new Set<number>(Array.isArray(board.reusableLocationPool) ? board.reusableLocationPool : []);
  // 将从有到无的仓位加入可重用池
  prevLocations.forEach((loc) => {
    if (!newLocations.has(loc)) pool.add(loc);
  });
  const updated: BoardState = {
    sortingDatabase: nonZeroItems,
    nextLocationNumber: board.nextLocationNumber,
    reusableLocationPool: Array.from(pool)
  };
  return updated;
}

// 清理数量为0的行，并在仓位为空时回收到 reusableLocationPool
export function cleanupEmpty(board: BoardState): BoardState {
  if (!board || !Array.isArray(board.sortingDatabase)) return board;
  const items = board.sortingDatabase.filter(it => Number(it.sortedQuantity || 0) > 0);
  return { ...board, sortingDatabase: items };
}

// 提交成功后：仅记录索引（不重复应用到看板，避免双计）
export function indexRecordDeltasAfterSubmit(params: { recordId: number; deltas: SortingDelta[] }) {
  const { recordId, deltas } = params;
  if (!recordId) return;
  const index = loadRecordIndex();
  index[String(recordId)] = {
    recordId,
    deltas: aggregateDeltas(deltas.map(d => ({ ...d, quantity: Math.abs(Number(d.quantity || 0)) }))),
    version: (index[String(recordId)]?.version || 0) + 1,
    updatedAt: new Date().toISOString()
  };
  saveRecordIndex(index);
}

// 作废/删除：回滚并清理索引
export function rollbackByRecordId(params: { recordId: number }) {
  const { recordId } = params;
  if (!recordId) return;
  const index = loadRecordIndex();
  const item = index[String(recordId)];
  if (!item || !Array.isArray(item.deltas) || item.deltas.length === 0) {
    console.warn('[SortingBoardSync] 当前本机无该入库记录的索引，跳过本地看板回滚', { recordId });
    return;
  }
  const board = loadBoardState();
  if (!board) {
    console.warn('[SortingBoardSync] 未发现本地分拣看板数据，跳过回滚');
    delete index[String(recordId)];
    saveRecordIndex(index);
    return;
  }
  const reverted = applyDeltasToBoard(board, item.deltas, -1);
  saveBoardState(reverted);
  // 双写以与扫描页的标签页存储保持一致，避免旧值覆盖
  saveBoardStateSecondary(reverted);
  delete index[String(recordId)];
  saveRecordIndex(index);
}

// 修改：根据新数量做差量应用，默认将增量/减量作用到“该记录首次出现的仓位”
export function applyModifyDiff(params: { recordId: number; newTotalsByUnit: Record<string, number> }) {
  const { recordId, newTotalsByUnit } = params;
  if (!recordId) return;
  const index = loadRecordIndex();
  const item = index[String(recordId)];
  if (!item || !Array.isArray(item.deltas) || item.deltas.length === 0) {
    console.warn('[SortingBoardSync] 本机无该记录索引，无法计算修改差量，建议忽略本地同步');
    return;
  }
  const board = loadBoardState();
  if (!board) {
    console.warn('[SortingBoardSync] 未发现本地分拣看板数据，跳过修改同步');
    return;
  }

  // 计算旧总量（按 unit 聚合）
  const oldTotals = new Map<string, number>();
  const unitToLocations = new Map<string, SortingDelta[]>();
  for (const d of item.deltas) {
    oldTotals.set(d.inventoryUnitCode, (oldTotals.get(d.inventoryUnitCode) || 0) + Number(d.quantity || 0));
    const arr = unitToLocations.get(d.inventoryUnitCode) || [];
    arr.push({ ...d });
    unitToLocations.set(d.inventoryUnitCode, arr);
  }

  // 计划差量操作
  const decOps: SortingDelta[] = [];
  const incOps: SortingDelta[] = [];
  const nextIndexDeltas: SortingDelta[] = [];

  Object.keys(newTotalsByUnit || {}).forEach(unitCode => {
    const newTotal = Math.max(0, Number(newTotalsByUnit[unitCode] || 0));
    const oldTotal = Math.max(0, Number(oldTotals.get(unitCode) || 0));
    const diff = newTotal - oldTotal;

    const locations = (unitToLocations.get(unitCode) || []).sort((a, b) => a.temporaryLocation - b.temporaryLocation);
    if (locations.length === 0) {
      // 没有历史位置：无法定位，跳过（最稳妥策略）
      return;
    }

    if (diff === 0) {
      // 数量不变：保留原分布
      locations.forEach(loc => nextIndexDeltas.push({ ...loc, quantity: Math.abs(Number(loc.quantity || 0)) }));
      return;
    }

    if (diff > 0) {
      // 增加：统一加到第一个出现的位置
      const first = locations[0];
      incOps.push({ inventoryUnitCode: unitCode, quantity: diff, temporaryLocation: first.temporaryLocation, sortingGroupSystemCode: first.sortingGroupSystemCode });
      // 新的索引分布：第一位置 = oldTotal + diff，其余清零
      nextIndexDeltas.push({ inventoryUnitCode: unitCode, quantity: oldTotal + diff, temporaryLocation: first.temporaryLocation, sortingGroupSystemCode: first.sortingGroupSystemCode });
      return;
    }

    // diff < 0：减少，从第一个位置开始扣减（最简单且可预测）
    let remaining = Math.abs(diff);
    for (const loc of locations) {
      if (remaining <= 0) break;
      const dec = Math.min(remaining, Number(loc.quantity || 0));
      if (dec > 0) {
        decOps.push({ inventoryUnitCode: unitCode, quantity: dec, temporaryLocation: loc.temporaryLocation, sortingGroupSystemCode: loc.sortingGroupSystemCode });
      }
      remaining -= dec;
    }
    // 更新索引分布：第一位置 = newTotal，其余尽量置零
    const first = locations[0];
    nextIndexDeltas.push({ inventoryUnitCode: unitCode, quantity: newTotal, temporaryLocation: first.temporaryLocation, sortingGroupSystemCode: first.sortingGroupSystemCode });
  });

  // 应用差量到看板
  let updated = board;
  if (decOps.length > 0) {
    updated = applyDeltasToBoard(updated, decOps, -1);
  }
  if (incOps.length > 0) {
    updated = applyDeltasToBoard(updated, incOps, +1);
  }
  saveBoardState(updated);
  // 双写以与扫描页的标签页存储保持一致，避免旧值覆盖
  saveBoardStateSecondary(updated);

  // 更新索引
  index[String(recordId)] = {
    recordId,
    deltas: aggregateDeltas(nextIndexDeltas),
    version: (item.version || 0) + 1,
    updatedAt: new Date().toISOString()
  };
  saveRecordIndex(index);
}

// 对外便捷方法：提交成功后，用扫描历史记录入索引
export function recordSortingBoardDeltasAfterSubmit(params: { recordId: number; scanHistory: Array<{ inventoryUnitCode: string; quantity: number; temporaryLocation: number; sortingGroupSystemCode?: string }> }) {
  const { recordId, scanHistory } = params;
  const deltas = buildDeltasFromScanHistory(scanHistory);
  indexRecordDeltasAfterSubmit({ recordId, deltas });
}

// 对外便捷方法：作废
export function rollbackSortingBoardForRecord(params: { recordId: number }) {
  rollbackByRecordId(params);
}

// 对外便捷方法：修改
export function updateSortingBoardForModify(params: { recordId: number; newTotalsByUnit: Record<string, number> }) {
  applyModifyDiff(params);
}


